import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import {
  Search, Star, Download, Package, Filter, Grid, List, Eye, ShoppingCart, Tag, Users, Calendar, Plus, Settings
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';
import toast from 'react-hot-toast';

const PluginStore = () => {
  const navigate = useNavigate();
  const [plugins, setPlugins] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPlugin, setSelectedPlugin] = useState(null);
  const [showPluginModal, setShowPluginModal] = useState(false);
  const [filters, setFilters] = useState({
    category: 'all',
    price: 'all',
    rating: 'all',
    status: 'all'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('popular');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [pluginsResponse, categoriesResponse] = await Promise.all([
          MockDataService.getPluginStore(),
          MockDataService.getPluginCategories()
        ]);

        if (pluginsResponse.success) {
          setPlugins(pluginsResponse.data);
        }
        if (categoriesResponse.success) {
          setCategories(categoriesResponse.data);
        }
      } catch (err) {
        setError("Failed to fetch plugin store data.");
        console.error("Error fetching plugin store data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleInstallPlugin = async (pluginId) => {
    try {
      const response = await MockDataService.installPlugin(pluginId);
      if (response.success) {
        // Update local state
        setPlugins(prev => prev.map(plugin => 
          plugin.id === pluginId 
            ? { ...plugin, status: 'installed', installedAt: new Date().toISOString() }
            : plugin
        ));
        toast.success('Plugin installed successfully!');
      } else {
        toast.error('Failed to install plugin');
      }
    } catch (err) {
      toast.error('Failed to install plugin');
      console.error("Error installing plugin:", err);
    }
  };

  const handleUninstallPlugin = async (pluginId) => {
    try {
      const response = await MockDataService.uninstallPlugin(pluginId);
      if (response.success) {
        // Update local state
        setPlugins(prev => prev.map(plugin => 
          plugin.id === pluginId 
            ? { ...plugin, status: 'available', installedAt: null }
            : plugin
        ));
        toast.success('Plugin uninstalled successfully!');
      } else {
        toast.error('Failed to uninstall plugin');
      }
    } catch (err) {
      toast.error('Failed to uninstall plugin');
      console.error("Error uninstalling plugin:", err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'installed': return 'success';
      case 'available': return 'outline';
      case 'updating': return 'secondary';
      default: return 'outline';
    }
  };

  const getPriceColor = (price) => {
    if (price === 0) return 'success';
    if (price < 50) return 'secondary';
    return 'destructive';
  };

  const filteredPlugins = plugins.filter(plugin => {
    const matchesCategory = filters.category === 'all' || plugin.category === filters.category;
    const matchesPrice = filters.price === 'all' || 
      (filters.price === 'free' && plugin.price === 0) ||
      (filters.price === 'paid' && plugin.price > 0);
    const matchesRating = filters.rating === 'all' || plugin.rating >= parseFloat(filters.rating);
    const matchesStatus = filters.status === 'all' || plugin.status === filters.status;
    const matchesSearch = plugin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         plugin.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesCategory && matchesPrice && matchesRating && matchesStatus && matchesSearch;
  });

  const sortedPlugins = [...filteredPlugins].sort((a, b) => {
    switch (sortBy) {
      case 'popular':
        return b.downloads - a.downloads;
      case 'rating':
        return b.rating - a.rating;
      case 'price':
        return a.price - b.price;
      case 'name':
        return a.name.localeCompare(b.name);
      case 'newest':
        return new Date(b.createdAt) - new Date(a.createdAt);
      default:
        return 0;
    }
  });

  if (loading) return <div className="p-6 text-center">Loading Plugin Store...</div>;
  if (error) return <div className="p-6 text-center text-red-500">Error: {error}</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Plugin Store</h1>
          <p className="text-gray-600 dark:text-gray-400">Browse and install plugins to extend your HelpDesk functionality.</p>
        </div>
        <div className="flex space-x-2">
          <Button 
            variant="outline" 
            onClick={() => navigate('/admin/plugins/installed')}
            className="flex items-center gap-2"
          >
            <Settings className="h-4 w-4" />
            Installed Plugins
          </Button>
          <Button variant="outline" onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}>
            {viewMode === 'grid' ? <List className="h-4 w-4" /> : <Grid className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Search & Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center space-x-2 flex-1 min-w-64">
              <Search className="h-4 w-4 text-gray-500" />
              <input
                type="text"
                placeholder="Search plugins..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-3 py-2 border rounded-md flex-1"
              />
            </div>
            
            <select
              value={filters.category}
              onChange={(e) => setFilters({...filters, category: e.target.value})}
              className="px-3 py-2 border rounded-md"
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>

            <select
              value={filters.price}
              onChange={(e) => setFilters({...filters, price: e.target.value})}
              className="px-3 py-2 border rounded-md"
            >
              <option value="all">All Prices</option>
              <option value="free">Free</option>
              <option value="paid">Paid</option>
            </select>

            <select
              value={filters.rating}
              onChange={(e) => setFilters({...filters, rating: e.target.value})}
              className="px-3 py-2 border rounded-md"
            >
              <option value="all">All Ratings</option>
              <option value="4.5">4.5+ Stars</option>
              <option value="4.0">4.0+ Stars</option>
              <option value="3.5">3.5+ Stars</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border rounded-md"
            >
              <option value="popular">Most Popular</option>
              <option value="rating">Highest Rated</option>
              <option value="price">Price: Low to High</option>
              <option value="name">Name A-Z</option>
              <option value="newest">Newest</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Plugins</CardTitle>
            <Package className="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{plugins.length}</div>
            <p className="text-xs text-gray-500">Available plugins</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Installed</CardTitle>
            <Download className="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {plugins.filter(p => p.status === 'installed').length}
            </div>
            <p className="text-xs text-gray-500">Currently installed</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Free Plugins</CardTitle>
            <Tag className="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {plugins.filter(p => p.price === 0).length}
            </div>
            <p className="text-xs text-gray-500">No cost plugins</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg. Rating</CardTitle>
            <Star className="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(plugins.reduce((sum, p) => sum + p.rating, 0) / plugins.length).toFixed(1)}
            </div>
            <p className="text-xs text-gray-500">Overall rating</p>
          </CardContent>
        </Card>
      </div>

      {/* Plugins Grid/List */}
      <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
        {sortedPlugins.map((plugin) => (
          <Card key={plugin.id} className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <CardTitle className="text-lg">{plugin.name}</CardTitle>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {plugin.description}
                  </p>
                </div>
                <Badge variant={getStatusColor(plugin.status)}>
                  {plugin.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`h-4 w-4 ${
                          i < Math.floor(plugin.rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'
                        }`}
                      />
                    ))}
                    <span className="ml-1 text-sm text-gray-600">{plugin.rating}</span>
                  </div>
                  <span className="text-sm text-gray-500">({plugin.reviews} reviews)</span>
                </div>
                <Badge variant={getPriceColor(plugin.price)}>
                  {plugin.price === 0 ? 'Free' : `$${plugin.price}`}
                </Badge>
              </div>

              <div className="flex items-center justify-between text-sm text-gray-500">
                <span className="flex items-center">
                  <Download className="h-4 w-4 mr-1" />
                  {plugin.downloads.toLocaleString()} downloads
                </span>
                <span className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  {new Date(plugin.createdAt).toLocaleDateString()}
                </span>
              </div>

              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedPlugin(plugin);
                    setShowPluginModal(true);
                  }}
                  className="flex-1"
                >
                  <Eye className="h-4 w-4 mr-1" />
                  View Details
                </Button>
                {plugin.status === 'installed' ? (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleUninstallPlugin(plugin.id)}
                    className="flex-1"
                  >
                    Uninstall
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => handleInstallPlugin(plugin.id)}
                    className="flex-1"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Install
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Plugin Details Modal */}
      <Modal isOpen={showPluginModal} onClose={() => setShowPluginModal(false)}>
        <div className="p-6 max-w-2xl">
          <h2 className="text-xl font-bold mb-4">Plugin Details</h2>
          {selectedPlugin && (
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-semibold">{selectedPlugin.name}</h3>
                  <p className="text-gray-600">{selectedPlugin.description}</p>
                </div>
                <Badge variant={getStatusColor(selectedPlugin.status)}>
                  {selectedPlugin.status}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <strong>Category:</strong> {selectedPlugin.category}
                </div>
                <div>
                  <strong>Version:</strong> {selectedPlugin.version}
                </div>
                <div>
                  <strong>Price:</strong> {selectedPlugin.price === 0 ? 'Free' : `$${selectedPlugin.price}`}
                </div>
                <div>
                  <strong>Downloads:</strong> {selectedPlugin.downloads.toLocaleString()}
                </div>
                <div>
                  <strong>Rating:</strong> {selectedPlugin.rating}/5 ({selectedPlugin.reviews} reviews)
                </div>
                <div>
                  <strong>Created:</strong> {new Date(selectedPlugin.createdAt).toLocaleDateString()}
                </div>
              </div>

              {selectedPlugin.features && (
                <div>
                  <h4 className="font-medium mb-2">Features:</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    {selectedPlugin.features.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedPlugin.requirements && (
                <div>
                  <h4 className="font-medium mb-2">Requirements:</h4>
                  <div className="bg-gray-50 p-3 rounded-md">
                    <pre className="text-sm whitespace-pre-wrap">{selectedPlugin.requirements}</pre>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setShowPluginModal(false)}>
                  Close
                </Button>
                {selectedPlugin.status === 'installed' ? (
                  <Button 
                    variant="destructive"
                    onClick={() => {
                      handleUninstallPlugin(selectedPlugin.id);
                      setShowPluginModal(false);
                    }}
                  >
                    Uninstall
                  </Button>
                ) : (
                  <Button 
                    onClick={() => {
                      handleInstallPlugin(selectedPlugin.id);
                      setShowPluginModal(false);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Install Plugin
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default PluginStore;